import { Search } from '@mui/icons-material';
import { Box, Checkbox, Grow, InputAdornment, List, ListItem, Stack, TextField, Typography } from '@mui/material';
import type React from 'react';
import { useEffect, useMemo, useState } from 'react';
import ClickAwayListener from '/@/components/click-await-listener';
import useDebounce from '/@/hooks/debounce/useDebounce';
import { _ } from '/@/locale';
import type { Country } from '/@/store/session/selectors';

interface Province {
    id: number;
    name: string;
    code?: string;
    zones: number[];
    children: Country[];
}

interface SearchInputProps {
    provincesWithLocations: Map<number, Province>;
    selectedLocations: Map<number, Set<number>>;
    toggleLocation: (provinceId: number, locationId: number) => void;
}

interface LocationItem {
    label: string;
    locationId: number;
    provinceId: number;
}

const SearchInput: React.FC<SearchInputProps> = ({ provincesWithLocations, selectedLocations, toggleLocation }) => {
    const [isDropdownOpen, setDropdownOpen] = useState<boolean>(false);
    const [searchText, setSearchText] = useState<string>('');

    // Debounce search text to limit filtering frequency
    const debouncedSearchText = useDebounce(searchText, 500);

    // Open the dropdown only when there is search text present
    useEffect(() => {
        setDropdownOpen(debouncedSearchText.trim().length > 0);
    }, [debouncedSearchText]);

    // Filter locations based on the debounced search text
    const filteredLocations: LocationItem[] = useMemo(() => {
        const trimmedText = debouncedSearchText.trim();
        if (trimmedText === '') return [];

        const lowerCaseSearch = trimmedText.toLowerCase();
        return Array.from(provincesWithLocations.values())
            .flatMap((province) =>
                province.children.map((location) => ({
                    label: `${location.name}, ${province.name}`,
                    locationId: location.id,
                    provinceId: province.id,
                }))
            )
            .filter(({ label }) => label.toLowerCase().includes(lowerCaseSearch));
    }, [provincesWithLocations, debouncedSearchText]);

    const handleLocationClick = (provinceId: number, locationId: number) => {
        toggleLocation(provinceId, locationId);
    };

    const renderLocationItem = ({ label, locationId, provinceId }: LocationItem) => {
        const isSelected = selectedLocations.get(provinceId)?.has(locationId) ?? false;

        return (
            <ListItem
                key={locationId}
                onClick={() => handleLocationClick(provinceId, locationId)}
                sx={isSelected ? { backgroundColor: '#F5F5F5', p: 1.5 } : { p: 1.5 }}
            >
                <Stack direction='row' alignItems='center' spacing={1.5}>
                    <Checkbox color='secondary' checked={isSelected} size='large' sx={{ p: 0 }} />
                    <Typography variant='subtitle1' color='primary'>
                        {label}
                    </Typography>
                </Stack>
            </ListItem>
        );
    };

    return (
        <Box position='relative'>
            <TextField
                placeholder={_('Buscar provincia, partido o departamento por nombre')}
                InputProps={{
                    startAdornment: (
                        <InputAdornment position='start'>
                            <Search fontSize='24px' />
                        </InputAdornment>
                    ),
                }}
                variant='outlined'
                onChange={(e) => setSearchText(e.currentTarget.value)}
                fullWidth
                color='secondary'
            />

            <ClickAwayListener onClickAway={() => setDropdownOpen(false)}>
                <Grow in={isDropdownOpen}>
                    <Box
                        sx={{
                            position: 'absolute',
                            top: '100%',
                            left: 0,
                            width: '100%',
                            zIndex: 1,
                            background: 'white',
                            border: (t) => `1px solid ${t.palette.divider}`,
                            maxHeight: 300,
                            overflowY: 'auto',
                        }}
                    >
                        <List sx={{ p: 0 }}>
                            {filteredLocations.length > 0 ? (
                                filteredLocations.map(renderLocationItem)
                            ) : (
                                <ListItem sx={{ p: 1.5 }}>
                                    <Typography variant='subtitle1' color='primary.mui.inactive'>
                                        {_('Sin resultados')}
                                    </Typography>
                                </ListItem>
                            )}
                        </List>
                    </Box>
                </Grow>
            </ClickAwayListener>
        </Box>
    );
};

export default SearchInput;
